import { format, parseISO } from "date-fns";
import { startCase } from "lodash";

import {
  ExportSummaryQuery,
  MretsRecAttributes,
  RegistryCode,
  RetirementCategory,
  RetirementSummaryQuery,
} from "../types/generated/graphql";
import { TraceXSupportedRegistries } from "../types/okta.types";
import { formatAttributes } from "./format-attributes";
import { RECOrderAttributeOptions } from "./format-rec-options";
import { volumeFormatter } from "./volume-formatter";

export type FormattedCommodityActionSummary<Registry extends TraceXSupportedRegistries> = {
  id: string;
  Vintage: string;
  Quantity: string;
  "Serial Numbers": string;
  Certifications: string;
  "Facility Effective Date": string;
  "Facility Expiry Date": string;
} & (Registry extends RegistryCode.Ercot
  ? {
      "Facility Identification Number": string;
      "Certificate Type": string;
      "Technology Type": string;
    }
  : {
      Project: string;
      Eligibilities: string;
      "Fuel Type": string;
      Location: string;
      COD: string;
    });

export const formatCommodityActionReport = (
  commodityActionDetails: RetirementSummaryQuery["retirementById"] | ExportSummaryQuery["exportRequestById"],
  registryCode: TraceXSupportedRegistries,
  recOptions?: RECOrderAttributeOptions,
): (FormattedCommodityActionSummary<RegistryCode.Ercot> | FormattedCommodityActionSummary<RegistryCode.Mrets>)[] => {
  const isActiveRegistryErcot = registryCode === RegistryCode.Ercot;
  // Helper function to determine which type of details we are dealing with
  const getDetails = (actionDetails: typeof commodityActionDetails) => {
    if ("commodityDetails" in actionDetails) {
      return actionDetails.commodityDetails;
    } else if ("exportRequestDetails" in actionDetails) {
      return actionDetails.exportRequestDetails;
    } else {
      return null;
    }
  };

  const details = getDetails(commodityActionDetails);

  if (!details) {
    return [];
  }

  return details.flatMap((detail) => {
    const attributes = detail.attributes as MretsRecAttributes;
    const formattedAttributes = formatAttributes({
      attributes,
      options: recOptions,
    });

    const commodities =
      detail.commodityTransactions?.map((transaction) => ({
        "Serial Numbers": `${transaction.commoditySerial.serialPrefix}-${transaction.commoditySerial.serialFrom} to ${transaction.commoditySerial.serialTo}`,
        "Facility Effective Date": transaction.generator.crsEffectiveDate
          ? format(parseISO(transaction.generator.crsEffectiveDate), "P")
          : "N/A",
        "Facility Expiry Date": transaction.generator.crsExpiryDate
          ? format(parseISO(transaction.generator.crsExpiryDate), "P")
          : "N/A",
        Quantity: volumeFormatter(transaction.volume),
      })) ?? [];

    return commodities.map((commodityInfo) => {
      const baseAttributes = {
        id: detail.id,
        Vintage: formattedAttributes.vintage,
        Certifications: joinOrReturnDefault(formattedAttributes.certifications),
        ...commodityInfo,
      };

      if (isActiveRegistryErcot) {
        return {
          ...baseAttributes,
          "Facility Identification Number": formattedAttributes.project,
          "Certificate Type": formattedAttributes.certificateType ?? "-",
          "Technology Type": joinOrReturnDefault(formattedAttributes.fuelSources),
        } as FormattedCommodityActionSummary<RegistryCode.Ercot>;
      } else {
        return {
          ...baseAttributes,
          Project: formattedAttributes.project,
          Eligibilities: joinOrReturnDefault(formattedAttributes.eligibilities),
          Location: formattedAttributes.location,
          COD: attributes.commencementDate ?? "-",
          "Fuel Type": joinOrReturnDefault(formattedAttributes.fuelSources),
        } as FormattedCommodityActionSummary<RegistryCode.Mrets>;
      }
    });
  });
};

const joinOrReturnDefault = (value: string[]) => (value?.some(Boolean) ? value.join(", ") : "-");

export const getRetirementReason = (retirementDetails: {
  reason: string;
  notes: string;
  compliancePeriod?: string | null;
}) => {
  const reason = retirementDetails.reason;
  const compliancePeriod = retirementDetails.compliancePeriod
    ? `(Compliance Period - ${retirementDetails.compliancePeriod})`
    : "";
  return `${reason} ${compliancePeriod}`.trim();
};

export const getRetirementType = (retirementType: string, retirementCategory: RetirementCategory) => {
  return `${retirementType} (${startCase(retirementCategory.toLowerCase())})`;
};
